import type { Recipe } from '@/service/recipe/types';
import { averageRating } from '@/service/recipe/findRecipes';
import {
  DAYS,
  type Day,
  type DayMeals,
  SLOTS,
  type WeekPlan,
} from '@/service/weekplan/types';

// Week starts are calendar dates ("2026-10-05"); move them in UTC so time
// zones and daylight saving never shift the day
export function shiftWeek(weekStart: string, weeks: number): string {
  const date = new Date(`${weekStart}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + weeks * 7);
  return date.toISOString().slice(0, 10);
}

// "Week of 5 Oct". getWeekStart turns local midnight into a UTC date, which
// east of UTC is the day before the first day; the label shows that day.
export function weekLabel(weekStart: string, weekStartDay: Day): string {
  const date = new Date(`${weekStart}T00:00:00Z`);
  const weekday = (date.getUTCDay() + 6) % 7; // Monday = 0
  if (weekday !== DAYS.indexOf(weekStartDay)) {
    date.setUTCDate(date.getUTCDate() + 1);
  }
  const day = date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  return `Week of ${day}`;
}

// The days of `plan` with the empty slots filled from `from`, and how many
// meals that copies; planned meals are never replaced
export function copyMissingMeals(
  plan: WeekPlan,
  from: WeekPlan,
): { days: Partial<Record<string, DayMeals>>; copied: number } {
  const days: Partial<Record<string, DayMeals>> = {};
  let copied = 0;
  for (const day of DAYS) {
    for (const slot of SLOTS) {
      const meal = from.meals[day]?.[slot];
      if (meal && !plan.meals[day]?.[slot]) {
        days[day] = { ...plan.meals[day], ...days[day], [slot]: meal };
        copied++;
      }
    }
  }
  return { days, copied };
}

// The week each recipe was last planned in, from the given plans
function lastPlanned(plans: WeekPlan[]): Map<string, string> {
  const last = new Map<string, string>();
  for (const plan of plans) {
    for (const day of Object.values(plan.meals)) {
      for (const meal of Object.values(day)) {
        const id = meal?.recipeId;
        if (id && (last.get(id) ?? '') < plan.weekStart) {
          last.set(id, plan.weekStart);
        }
      }
    }
  }
  return last;
}

// Recipes not planned in the last `weeks` weeks before this one: highly
// rated first, then the ones planned longest ago (or never)
export function suggestRecipes(
  recipes: Recipe[],
  plans: WeekPlan[],
  weekStart: string,
  { weeks = 4, limit = 5 } = {},
): Recipe[] {
  const last = lastPlanned(plans);
  const since = shiftWeek(weekStart, -weeks);
  return recipes
    .filter((recipe) => (last.get(recipe.id) ?? '') < since)
    .sort(
      (a, b) =>
        (averageRating(b) ?? 0) - (averageRating(a) ?? 0) ||
        (last.get(a.id) ?? '').localeCompare(last.get(b.id) ?? '') ||
        a.title.localeCompare(b.title),
    )
    .slice(0, limit);
}
